import type {
  CellContext,
  CellData,
  ColumnDef,
  ColumnDefTemplate,
  RowData,
  StringOrTemplateHeader,
} from '@tanstack/react-table';
import { useMemo } from 'react';

import { makeAccessorFunction } from './makeAccessorFunction.js';
import { LogContentHighlight } from '../../utils/log-content-highlighting.js';
import { replaceUrlsWithLinks } from '../../utils/replace-urls-with-links.js';
import {
  applyColumnFormatters,
  formatCellValue,
} from '../features/CellFormatter/CellFormatter.js';
import {
  applyColumnTypes,
  getAlignmentBasedOnType,
  isDatatableLogContentColumnConfig,
} from '../features/ColumnTypes/ColumnTypes.js';
import { SPACER_COLUMN } from '../features/Layout/LayoutColumnDef.js';
import { applySortTypes } from '../features/Sorting/sort-types.js';
import type { TDataTableFeatures } from '../features/types/features.types.js';
import type {
  DataTableCustomCell,
  DataTableColumnDef,
  DataTableDisplayColumnDef,
  DataTableGroupColumnDef,
  DataTableRowData,
} from '../public.api.js';

/**
 * Type guard to determine if a given column definition is a `DataTableGroupColumnDef`.
 * @public
 */
export function isDataTableGroupColumnDef<
  TData extends DataTableRowData,
  TValue = unknown,
>(columnDef: unknown): columnDef is DataTableGroupColumnDef<TData, TValue> {
  return (
    columnDef !== null &&
    typeof columnDef === 'object' &&
    'columns' in columnDef &&
    Array.isArray((columnDef as { columns: Array<unknown> }).columns)
  );
}

/**
 * Type guard to determine if a given column definition is a `DataTableDisplayColumnDef`.
 * @public
 */
export function isDataTableDisplayColumnDef<
  TData extends DataTableRowData,
  TValue = unknown,
>(columnDef: unknown): columnDef is DataTableDisplayColumnDef<TData, TValue> {
  return (
    columnDef !== null &&
    typeof columnDef === 'object' &&
    'accessor' in columnDef
  );
}

/**
 * Helper function to transform the public accessor key to the correct
 * member config for the tanstack/react-table ColumnDef.
 */
function splitAccessor<TData extends DataTableRowData, TValue = unknown>(
  column: DataTableDisplayColumnDef<TData, TValue>,
): {
  accessorKey?: string;
  accessorFn?: (row: TData) => TValue;
} {
  if (typeof column.accessor === 'string') {
    if (column.accessor.includes('.') || column.accessor.includes('[')) {
      return {
        accessorFn: makeAccessorFunction(column.accessor),
      };
    }

    return {
      accessorKey: column.accessor,
    };
  }

  if (typeof column.accessor === 'function') {
    return {
      accessorFn: column.accessor,
    };
  }
  return {};
}

/**
 * Maps the given header renderer to the internal header renderer and prevents
 * leaking.
 */
function createHeaderRenderer<TData extends DataTableRowData, TValue = unknown>(
  header: DataTableColumnDef<TData>['header'],
): StringOrTemplateHeader<TDataTableFeatures, RowData, TValue> | undefined {
  // We need to map the function from the outside here in order to not leak
  // tanstack table internals.
  if (typeof header === 'function') {
    return () => header();
  }
  return header;
}

/**
 * Prepares all exposed functions for the custom cell renderer.
 */
function createCellRenderer<TData extends RowData, TValue>(
  consumerCustomCell: DataTableCustomCell<TData, TValue>,
  consumerColumnDef: DataTableDisplayColumnDef<TData, TValue>,
): ColumnDefTemplate<CellContext<TDataTableFeatures, TData, TValue>> {
  return ({ getValue, row, column }) => {
    const lineWrap = column.getIsLineWrapped();

    return consumerCustomCell({
      value: getValue(),
      rowIndex: row.index,
      rowData: row.original,
      rowId: row.id,
      format: (value) => formatCellValue(value, consumerColumnDef),
      formatLogContent: (logContent: string) => {
        const truncationLimit = isDatatableLogContentColumnConfig(
          consumerColumnDef.config,
        )
          ? consumerColumnDef.config.truncationLimit
          : undefined;
        return (
          <LogContentHighlight
            logContent={logContent}
            lineWrap={lineWrap}
            truncationLimit={truncationLimit}
          />
        );
      },
      detectLinks: replaceUrlsWithLinks,
      isLineWrapped: lineWrap,
    });
  };
}

/**
 * Helper function to convert from the public `DataTableDisplayColumnDef` to the internally used
 * ColumnDef.
 */
function createDisplayColumnDef(
  columnDef: DataTableDisplayColumnDef<DataTableRowData, unknown>,
): ColumnDef<TDataTableFeatures, RowData, CellData> {
  const hasFormatter =
    columnDef.columnType !== 'meterbar' &&
    columnDef.columnType !== 'sparkline' &&
    columnDef.columnType !== 'gantt' &&
    !!columnDef.formatter;

  const composedDef: ColumnDef<TDataTableFeatures, RowData, CellData> = {
    id: columnDef.id,
    header: createHeaderRenderer(columnDef.header),
    label: columnDef.label,
    width: columnDef.width,
    minWidth: columnDef.minWidth,
    maxWidth: columnDef.maxWidth,
    alignment:
      columnDef.alignment ?? getAlignmentBasedOnType(columnDef.columnType),
    enableSorting: columnDef.disableSorting ? !columnDef.disableSorting : true,
    sortAccessor: columnDef.sortAccessor,
    sortDescFirst: columnDef.sortDescFirst,
    invertSorting: columnDef.sortInverted,
    enableHiding: !columnDef.disableColumnHiding,
    thresholds: columnDef.thresholds,
    meta: {
      config: columnDef.config,
      hasFormatter,
      hasCustomCellRenderer: columnDef.cell !== undefined,
      hasCustomHeaderRenderer: typeof columnDef.header !== 'string',
    },
    ...splitAccessor(columnDef),
    ...applySortTypes(columnDef),
  };

  if (columnDef.columnType) {
    composedDef.columnType = columnDef.columnType;
  }

  if (columnDef.cell) {
    const customCell = columnDef.cell;

    if (typeof customCell === 'string') {
      composedDef.cell = customCell;
      return composedDef;
    }

    if (typeof customCell === 'function') {
      return {
        ...composedDef,
        cell: createCellRenderer(customCell, columnDef),
      };
    }

    return composedDef;
  }

  return {
    ...composedDef,
    ...applyColumnTypes(columnDef, composedDef),
    ...applyColumnFormatters(columnDef),
  };
}

/**
 * Helper hook that will calculate which columns are enabled within the DataTable
 * @internal
 */
export function useColumns(
  propColumns: DataTableColumnDef<DataTableRowData, unknown>[],
): [
  ColumnDef<TDataTableFeatures, RowData, CellData>[],
  Map<string, DataTableColumnDef<DataTableRowData, unknown>>,
] {
  return useMemo(() => {
    const createdColumns: ColumnDef<TDataTableFeatures, RowData, CellData>[] =
      [];
    const propColumnMap = new Map<
      string,
      DataTableColumnDef<DataTableRowData, CellData>
    >();

    // Iterate through the columns and create the definitions required
    for (const firstLevelColumn of propColumns) {
      propColumnMap.set(firstLevelColumn.id, firstLevelColumn);
      if (
        isDataTableGroupColumnDef<DataTableRowData, CellData>(firstLevelColumn)
      ) {
        const groupColumn: ColumnDef<TDataTableFeatures, RowData, CellData> = {
          id: firstLevelColumn.id,
          header: createHeaderRenderer<RowData, CellData>(
            firstLevelColumn.header,
          ),
          width: firstLevelColumn.width,
          enableSorting: false,
          columns: firstLevelColumn.columns?.map((childColumn) =>
            createDisplayColumnDef(childColumn),
          ),
          alignment: firstLevelColumn.alignment,
          meta: {
            hasCustomHeaderRenderer:
              typeof firstLevelColumn.header !== 'string',
          },
        };
        createdColumns.push(groupColumn);

        for (const secondLevelColumn of firstLevelColumn.columns ?? []) {
          propColumnMap.set(secondLevelColumn.id, secondLevelColumn);
        }
      } else if (isDataTableDisplayColumnDef(firstLevelColumn)) {
        createdColumns.push(createDisplayColumnDef(firstLevelColumn));
      }
    }

    // Insert a last column to spread
    if (createdColumns.length > 0) {
      createdColumns.push(SPACER_COLUMN);
    }

    return [createdColumns, propColumnMap] as const;
  }, [propColumns]);
}
